import { API_CONFIG } from '../../../config/api';
import {
  FundoForm, Classe, Conta, VinculoPrestador, Condicoes, DocumentoAnexo, DocumentoVersao,
  condicoesVazia,
} from './types';
import { maskCnpj } from './fields';
import { FundoListItem, Opt } from './mock';

const baseUrl = (): string => {
  const u = API_CONFIG.LIQUIDOS_URL;
  if (!u) throw new Error('REACT_APP_LIQUIDOS_API_URL não configurada.');
  return u.replace(/\/+$/, '');
};

const headers = (): Record<string, string> => {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (API_CONFIG.LIQUIDOS_API_KEY) h['x-api-key'] = API_CONFIG.LIQUIDOS_API_KEY as string;
  return h;
};

export const getUsuarioId = (): number => {
  const raw = localStorage.getItem('id_usuario');
  const id = Number(raw);
  if (!raw || !Number.isFinite(id) || id <= 0) {
    throw new Error('Usuário não identificado (id_usuario ausente). Faça login novamente.');
  }
  return id;
};

interface ApiError extends Error { status?: number; details?: unknown; }

export async function call(method: string, path: string, body?: unknown): Promise<any> {
  const res = await fetch(`${baseUrl()}${path}`, {
    method,
    headers: headers(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const txt = await res.text();
  const json = txt ? JSON.parse(txt) : null;
  if (!res.ok) {
    const err: ApiError = new Error(json?.error || json?.message || `Erro ${res.status}`);
    err.status = res.status;
    err.details = json?.details;
    throw err;
  }
  return json && typeof json === 'object' && 'data' in json ? json.data : json;
}

const nz = (s?: string): string | undefined => (s && s.trim() ? s.trim() : undefined);
// datas → ISO (yyyy-mm-dd). Aceita dd/mm/yyyy (vindo da edição) e já-ISO (cadastro).
const dataIso = (s?: string): string | undefined => {
  const v = (s || '').trim();
  if (!v) return undefined;
  const br = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return br ? `${br[3]}-${br[2]}-${br[1]}` : v;
};
const digitos = (s?: string): string | undefined => {
  const d = (s || '').replace(/\D/g, '');
  return d || undefined;
};
// Decimais vão como STRING já arredondada na escala da coluna. Mandar float (ex.: 0.016)
// fazia o backend estourar "no more than N digits" (Decimal(0.0160000000000000003...)).
const moeda = (s?: string, casas = 2): string | undefined => {
  if (!s || !s.trim()) return undefined;
  const n = Number(s.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n.toFixed(casas) : undefined;
};
const fracao = (s?: string, casas = 8): string | undefined => {
  if (!s || !s.trim()) return undefined;
  const n = Number(s.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? (n / 100).toFixed(casas) : undefined;
};

const mapCondicoes = (c: Condicoes) => ({
  tx_adm: fracao(c.tx_adm),
  tx_custodia: fracao(c.tx_custodia),
  tx_performance: fracao(c.tx_performance),
  tx_adm_fiduciaria: fracao(c.tx_adm_fiduciaria),
  tx_adm_minima: moeda(c.tx_adm_minima),
  valor_minimo_adm_fiduciario: moeda(c.valor_minimo_adm_fiduciario),
  vlr_primeira_cota: moeda(c.vlr_primeira_cota, 6),
  aplicacao_inicial: moeda(c.aplicacao_inicial),
  aplicacao_minima: moeda(c.aplicacao_minima),
  saldo_minimo: moeda(c.saldo_minimo),
  resgate_minimo: moeda(c.resgate_minimo),
  d_cotizacao_aplic: nz(c.d_cotizacao_aplic),
  d_cotizacao_resgate: nz(c.d_cotizacao_resgate),
  d_pgto_resgate: nz(c.d_pgto_resgate),
  taxa_de_saida: !!c.taxa_de_saida,
  taxa_saida_valor: moeda(c.taxa_saida_valor),
  saida_antecipada_cotizacao: nz(c.saida_antecipada_cotizacao),
  saida_antecipada_pgto: nz(c.saida_antecipada_pgto),
  vigente_desde: dataIso(c.vigente_desde),
});

const mapClasse = (c: Classe) => ({
  id: c.id,
  subclasse: c.subclasse || 'Única',
  cnpj_classe: digitos(c.cnpj_classe),
  nome: nz(c.nome),
  tipo_cota: nz(c.tipo_cota),
  condominio: nz(c.condominio),
  publico_alvo: nz(c.publico_alvo),
  status_aplicacao: nz(c.status_aplicacao),
  calendario: nz(c.calendario),
  cod_cetip: nz(c.cod_cetip),
  isin: nz(c.isin),
  benchmark: nz(c.benchmark),
  spread: fracao(c.spread, 6),
  rating: nz(c.rating),
  razao_garantia: fracao(c.razao_garantia, 4),
  razao_garantia_target: fracao(c.razao_garantia_target, 4),
  divulgacao_site: !!c.divulgacao_site,
  is_active: !!c.is_active,
  dt_inicio: dataIso(c.dt_inicio),
  dt_encerramento: dataIso(c.dt_encerramento),
  condicoes: mapCondicoes(c.condicoes),
});

const mapConta = (c: Conta) => ({
  id: c.id,
  tipo: c.tipo,
  codigo: nz(c.codigo),
  banco: nz(c.banco),
  agencia: nz(c.agencia),
  numero_conta: nz(c.numero_conta),
  favorecido: nz(c.favorecido),
});

const mapPrestador = (p: VinculoPrestador) => ({
  id: p.id,
  prestador_id: p.prestador_id ? Number(p.prestador_id) : undefined,
  papel: p.papel,
  data_inicio: dataIso(p.data_inicio),
  data_fim: dataIso(p.data_fim),
});

const mapFundo = (form: FundoForm) => ({
  cnpj: digitos(form.cnpj),
  razao_social: form.razao_social?.trim(),
  forma_juridica: nz(form.forma_juridica),
  moeda: form.moeda || 'BRL',
  master_fic: nz(form.master_fic),
  nome_fantasia: nz(form.nome_fantasia),
  codigo_anbima: nz(form.codigo_anbima),
  classe_anbima_n1: nz(form.classe_anbima_n1),
  classe_anbima_n2: nz(form.classe_anbima_n2),
  classe_anbima_n3: nz(form.classe_anbima_n3),
  classe_anga_1: nz(form.classe_anga_1),
  classe_anga_2: nz(form.classe_anga_2),
  voice: nz(form.voice),
  juros: nz(form.juros),
  observacao: nz(form.observacao),
  data_lancamento: dataIso(form.data_lancamento),
  data_encerramento: dataIso(form.data_encerramento),
  is_active: !!form.is_active,
  count_on_aum: !!form.count_on_aum,
  gera_lamina: !!form.gera_lamina,
  paga_gestao: !!form.paga_gestao,
  classes: (form.classes || []).map(mapClasse),
  contas: (form.contas || []).map(mapConta),
  prestadores: (form.prestadores || []).map(mapPrestador),
});

async function enviarDocumentosNovos(
  fundoId: number,
  docs: DocumentoAnexo[],
  usuario_id: number,
): Promise<string | undefined> {
  for (const d of docs) {
    if (!d._file) continue;
    try {
      const up = await call('POST', '/documentos/upload-url', {
        usuario_id,
        tipo_documento: d.tipo,
        nome_arquivo: d._file.name,
        mime_type: d._file.type || undefined,
      });
      const put = await fetch(up.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': d._file.type || 'application/octet-stream' },
        body: d._file,
      });
      if (!put.ok) throw new Error(`upload ao S3 falhou (${put.status})`);
      await call('POST', '/documentos', {
        usuario_id,
        tipo_documento: d.tipo,
        titulo: d.titulo || d.tipo,
        data_referencia: nz(d.data_referencia),
        vinculo: { fundo_id: fundoId },
        arquivo: { s3_key: up.s3_key, nome_arquivo: d._file.name },
      });
    } catch (e: any) {
      return `${d.tipo}: ${e?.message ?? 'erro'}`;
    }
  }
  return undefined;
}

export interface CadastroResult { fundoId: number; erroDocs?: string; }

export async function cadastrarFundo(form: FundoForm): Promise<CadastroResult> {
  const usuario_id = getUsuarioId();
  const resp = await call('POST', '/fundos', { usuario_id, ...mapFundo(form) });
  const fundoId = resp?.fundo_id;
  const erroDocs = await enviarDocumentosNovos(fundoId, form.documentos || [], usuario_id);
  return { fundoId, erroDocs };
}

// Usa ?id= no recurso /fundos (o recurso path /fundos/{id} não existe no gateway → CORS quebra).
export async function atualizarFundo(id: number | string, form: FundoForm): Promise<void> {
  const usuario_id = getUsuarioId();
  await call('PUT', `/fundos?id=${id}`, { usuario_id, ...mapFundo(form) });
}

// Arquiva o fundo. No backend é soft-delete (marca deleted_at) em cascata.
export async function removerFundo(id: number | string): Promise<void> {
  const usuario_id = getUsuarioId();
  await call('DELETE', `/fundos?id=${id}`, { usuario_id });
}

const toNum = (x: unknown): number | undefined => {
  if (x === null || x === undefined || x === '') return undefined;
  const n = typeof x === 'number' ? x : Number(x);
  return Number.isFinite(n) ? n : undefined;
};
const fmtFrac = (x: unknown): string | undefined => {
  const n = toNum(x);
  if (n === undefined) return undefined;
  return (n * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
};
const fmtMoney = (x: unknown): string | undefined => {
  const n = toNum(x);
  if (n === undefined) return undefined;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtDateBr = (x: unknown): string | undefined => {
  if (!x) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(x));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(x);
};
const fmtBytes = (x: unknown): string | undefined => {
  const n = toNum(x);
  if (n === undefined) return undefined;
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1).replace('.', ',')} KB`;
  return `${(n / 1048576).toFixed(1).replace('.', ',')} MB`;
};
const str = (x: unknown): string | undefined => {
  const s = x === null || x === undefined ? '' : String(x).trim();
  return s ? s : undefined;
};

let _key = 0;
const k = (p: string): string => `${p}${++_key}`;

const condicoesFromApi = (c: any): Condicoes => ({
  tx_adm: fmtFrac(c.tx_adm),
  tx_custodia: fmtFrac(c.tx_custodia),
  tx_performance: fmtFrac(c.tx_performance),
  tx_adm_fiduciaria: fmtFrac(c.tx_adm_fiduciaria),
  tx_adm_minima: fmtMoney(c.tx_adm_minima),
  valor_minimo_adm_fiduciario: fmtMoney(c.valor_minimo_adm_fiduciario),
  vlr_primeira_cota: fmtMoney(c.vlr_primeira_cota),
  aplicacao_inicial: fmtMoney(c.aplicacao_inicial),
  aplicacao_minima: fmtMoney(c.aplicacao_minima),
  saldo_minimo: fmtMoney(c.saldo_minimo),
  resgate_minimo: fmtMoney(c.resgate_minimo),
  d_cotizacao_aplic: str(c.d_cotizacao_aplic),
  d_cotizacao_resgate: str(c.d_cotizacao_resgate),
  d_pgto_resgate: str(c.d_pgto_resgate),
  taxa_de_saida: !!c.taxa_de_saida,
  taxa_saida_valor: fmtMoney(c.taxa_saida_valor),
  saida_antecipada_cotizacao: str(c.saida_antecipada_cotizacao),
  saida_antecipada_pgto: str(c.saida_antecipada_pgto),
  vigente_desde: fmtDateBr(c.vigente_desde),
});

const classeFromApi = (c: any): Classe => ({
  _key: k('c'),
  id: c.id,
  subclasse: c.subclasse || 'Única',
  nome: str(c.nome),
  cnpj_classe: c.cnpj_classe ? maskCnpj(String(c.cnpj_classe)) : undefined,
  tipo_cota: str(c.tipo_cota),
  condominio: str(c.condominio),
  publico_alvo: str(c.publico_alvo),
  status_aplicacao: str(c.status_aplicacao),
  calendario: str(c.calendario),
  cod_cetip: str(c.cod_cetip),
  isin: str(c.isin),
  benchmark: str(c.benchmark),
  spread: fmtFrac(c.spread),
  rating: str(c.rating),
  razao_garantia: fmtFrac(c.razao_garantia),
  razao_garantia_target: fmtFrac(c.razao_garantia_target),
  divulgacao_site: !!c.divulgacao_site,
  is_active: !!c.is_active,
  dt_inicio: fmtDateBr(c.dt_inicio),
  dt_encerramento: fmtDateBr(c.dt_encerramento),
  condicoes: c.condicoes ? condicoesFromApi(c.condicoes) : condicoesVazia(),
});

const contaFromApi = (c: any): Conta => ({
  _key: k('ct'),
  id: c.id,
  tipo: c.tipo || '',
  codigo: str(c.codigo),
  banco: str(c.banco),
  agencia: str(c.agencia),
  numero_conta: str(c.numero_conta),
  favorecido: str(c.favorecido),
});

const prestadorFromApi = (p: any): VinculoPrestador => ({
  _key: k('p'),
  id: p.id,
  prestador_id: p.prestador_id ?? undefined,
  prestador_nome: str(p.prestador_nome),
  papel: p.papel || '',
  data_inicio: fmtDateBr(p.data_inicio),
  data_fim: fmtDateBr(p.data_fim),
});

const documentoFromApi = (d: any): DocumentoAnexo => {
  const versoes: DocumentoVersao[] = (d.versoes || []).map((ver: any) => ({
    numero_versao: ver.numero_versao,
    nome_arquivo: ver.nome_arquivo,
    data_referencia: fmtDateBr(ver.created_at),
    tamanho: fmtBytes(ver.tamanho_bytes),
    is_current: !!ver.is_current,
  }));
  const atual = versoes.find(v => v.is_current) ?? versoes[0];
  return {
    tipo: d.tipo_documento,
    documento_id: d.documento_id ?? undefined,
    titulo: str(d.titulo) ?? d.tipo_documento,
    nome_arquivo: str(d.nome_arquivo) ?? atual?.nome_arquivo,
    data_referencia: fmtDateBr(d.data_referencia) ?? atual?.data_referencia,
    versoes,
  };
};

export function apiFundoToForm(d: any): FundoForm {
  return {
    cnpj: d.cnpj ? maskCnpj(String(d.cnpj)) : '',
    razao_social: d.razao_social ?? '',
    nome_fantasia: str(d.nome_fantasia),
    forma_juridica: str(d.forma_juridica),
    moeda: d.moeda || 'BRL',
    master_fic: str(d.master_fic),
    codigo_anbima: str(d.codigo_anbima),
    data_lancamento: fmtDateBr(d.data_lancamento),
    data_encerramento: fmtDateBr(d.data_encerramento),
    voice: str(d.voice),
    juros: str(d.juros),
    observacao: str(d.observacao),
    is_active: !!d.is_active,
    count_on_aum: !!d.count_on_aum,
    gera_lamina: !!d.gera_lamina,
    paga_gestao: !!d.paga_gestao,
    classe_anbima_n1: str(d.classe_anbima_n1),
    classe_anbima_n2: str(d.classe_anbima_n2),
    classe_anbima_n3: str(d.classe_anbima_n3),
    classe_anga_1: str(d.classe_anga_1),
    classe_anga_2: str(d.classe_anga_2),
    classes: (d.classes || []).map(classeFromApi),
    contas: (d.contas || []).map(contaFromApi),
    prestadores: (d.prestadores || []).map(prestadorFromApi),
    documentos: (d.documentos || []).map(documentoFromApi),
  };
}

const jurosLabel = (v?: string): string =>
  v === 'simples' ? 'Simples' : v === 'compostos' ? 'Compostos' : (v || '—');

function apiRowToListItem(r: any): FundoListItem {
  return {
    id: r.id,
    cnpj: r.cnpj ?? '',
    razao_social: r.razao_social ?? '',
    nome_fantasia: r.nome_fantasia ?? '',
    codigo_anbima: r.codigo_anbima ?? '',
    moeda: r.moeda ?? '',
    administradora: r.administrador ?? '—',
    gestor: r.gestor ?? '—',
    classe_anbima: r.classe_anbima_n1 ?? '',
    classe_anga_1: r.classe_anga_1 ?? '',
    status: r.is_active ? 'Ativo' : 'Inativo',
    condominio: r.condominio === 'ABERTO' ? 'Aberto' : r.condominio === 'FECHADO' ? 'Fechado' : (r.condominio ?? '—'),
    conta_cetip: r.conta_cetip ?? '—',
    conta_selic: r.conta_selic ?? '—',
    voice: str(r.voice) ?? '—',
    juros: jurosLabel(r.juros),
    forma_juridica: r.forma_juridica ?? '',
    data_lancamento: r.data_lancamento ? String(r.data_lancamento).slice(0, 10) : '',
  };
}

// Consulta pública de CNPJ (BrasilAPI, sem chave/CORS liberado) p/ autopreencher
// razão social e nome fantasia. Retorna null em qualquer falha (não bloqueia o form).
export interface CnpjPublico { razao_social?: string; nome_fantasia?: string; }
export async function consultarCnpjPublico(cnpj: string): Promise<CnpjPublico | null> {
  const d = (cnpj || '').replace(/\D/g, '');
  if (d.length !== 14) return null;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 8000); // não pendura se a API travar
  try {
    const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${d}`, { signal: ctrl.signal });
    if (!res.ok) return null;
    const j = await res.json();
    return { razao_social: str(j?.razao_social), nome_fantasia: str(j?.nome_fantasia) };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// Opções de prestadores vindas da API (GET /prestadores) — substitui o antigo mock.
// O id é a PK real; evita o FK quebrado que gerava o "erro de gestora" no cadastro.
export async function listarPrestadorOpts(): Promise<Opt[]> {
  const data: any[] = (await call('GET', '/prestadores')) ?? [];
  return data
    .map((r) => ({ value: String(r.id), label: str(r.nome) ?? `#${r.id}` }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

export async function listarFundos(q?: string): Promise<FundoListItem[]> {
  const qs = q && q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
  const data: any[] = (await call('GET', `/fundos${qs}`)) ?? [];
  return data.map(apiRowToListItem);
}

export async function detalharFundo(id: number | string): Promise<FundoForm> {
  const data = await call('GET', `/fundos?id=${encodeURIComponent(String(id))}`);
  return apiFundoToForm(data ?? {});
}

export async function enviarDocumento(
  fundoId: number,
  anexo: { tipo: string; titulo?: string; documento_id?: number },
  file: File,
): Promise<{ documento_id: number; numero_versao: number }> {
  const usuario_id = getUsuarioId();

  const up = await call('POST', '/documentos/upload-url', {
    usuario_id,
    tipo_documento: anexo.tipo,
    nome_arquivo: file.name,
    mime_type: file.type || undefined,
  });

  const put = await fetch(up.upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!put.ok) throw new Error(`upload ao S3 falhou (${put.status})`);

  const body: any = { usuario_id, arquivo: { s3_key: up.s3_key, nome_arquivo: file.name } };
  if (anexo.documento_id) {
    body.documento_id = anexo.documento_id;
  } else {
    body.tipo_documento = anexo.tipo;
    body.titulo = anexo.titulo || anexo.tipo;
    body.vinculo = { fundo_id: fundoId };
  }

  const reg = await call('POST', '/documentos', body);
  return { documento_id: reg.documento_id, numero_versao: reg.numero_versao };
}

export async function baixarDocumento(documento_id: number, numero_versao?: number): Promise<void> {
  const usuario_id = getUsuarioId();
  const resp = await call('POST', '/documentos/download-url', { usuario_id, documento_id, numero_versao });
  const url: string | undefined = resp?.download_url;
  if (!url) throw new Error('URL de download não retornada');
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  if (resp.nome_arquivo) a.download = resp.nome_arquivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
